<template>
  <app-page
    :title="$t('shifts.upload.pageTitle')"
    :subtitle="$t('shifts.upload.pageSubtitle')"
    icon="calendar-circle-plus"
    :previous="[{ label: $t('shifts.previousLabel'), to: { name: 'shifts' } }]"
  >
    <bulk-upload name="shift" :schema="fieldSchema" :post-function="upload" />
  </app-page>
</template>

<script setup>
import moment from 'moment';
import { computed, onBeforeMount, ref } from 'vue';
import { useRouter } from 'vue-router/composables';
import ApiClient from '@/ApiClient';
import tracker from '@/Tracker';
import AppPage from '@/components/AppPage.vue';
import BulkUpload from '@/components/BulkUpload.vue';
import i18n from '@/i18n';
import IntegrationsState from '@/state/IntegrationsState';
import State from '@/state/State';
import { unFormatCurrency } from '@/utils/common';
import { formatDateIfValid } from '@/utils/date';

const $router = useRouter();

const loading = ref(false);

// A computed, not a plain const: i18n.t() below must re-evaluate on a runtime locale
// switch, not freeze at whatever locale was active when this component first mounted.
const fieldSchema = computed(() => [
  {
    key: 'employee_id',
    label: 'employee_id',
    description: i18n.t('shifts.upload.employeeIdDescription'),
    example: 'WS-00001',
    type: 'Text',
    required: true,
  },
  {
    key: 'worked_on',
    label: 'worked_on',
    description: i18n.t('shifts.upload.workedOnDescription'),
    example: '2022-02-28',
    type: 'Date',
    required: true,
    formatter: value => formatDateIfValid(value),
    validator: value => moment(value, 'YYYY-MM-DD', true).isValid(),
  },
  {
    key: 'hours',
    label: 'hours',
    description: i18n.t('shifts.upload.hoursDescription'),
    example: '7.5',
    type: 'Number',
    required: true,
    validator: value => !isNaN(value) && value >= 0,
  },
  {
    key: 'wages',
    label: 'wages',
    description: i18n.t('shifts.upload.wagesDescription'),
    example: '75.36',
    type: 'Currency',
    required: true,
    formatter: unFormatCurrency,
    validator: value => !isNaN(value) && value >= 0,
  },
]);

const upload = async (companyId, fileName, fileContent, metadata) => {
  await tracker.trackEvent('sme_live_add_shifts');
  return ApiClient.smeAddBulkShifts(companyId, fileName, fileContent, metadata);
};

onBeforeMount(async () => {
  loading.value = true;
  await ApiClient.getIntegrations(State.state.company.company_id);
  const entities = IntegrationsState.getBrowserUploadEntities();
  if (entities?.includes('shifts')) {
    $router.replace({ name: 'sftp-bulk-upload', query: { entity: 'shifts' } });
  }
  loading.value = false;
});
</script>
